import React, { useState, useEffect, useMemo, useReducer } from "react";
import { useEmailInfo } from "../../context/emailContext";
import styled from "styled-components";
import { devices, defaultTheme } from "../../styles";

const defaultInfoObject = {
  email: "",
  patientName: "",
};

const inputs = [
  {
    label: "Your email",
    placeholder: "Please enter your email",
    key: "email",
    name: "reply_to",
  },
  {
    label: "Additional details",
    placeholder: "Please enter additional details",
    key: "patientName",
    name: "name",
  },
];

const reducer = (state, action) => {
  const { key, value } = action;

  return { ...state, [key]: value };
};

export default function PrescriptionInfoForm({
  showError,
  setShowError,
  setIsError,
}) {
  const [state, dispatch] = useReducer(reducer, defaultInfoObject);
  const [fieldErrors, setFieldErrors] = useState({}); // Add state for individual field errors
  const optionalKeys = useMemo(() => {
    return inputs.filter((item) => item.optional).map((item) => item.key);
  }, []);

  const [errorMessage, setErrorMessage] = useState(
    "Please complete all mandatory fields to continue"
  );

  const [errors, setErrors] = useState([]);

  useEffect(() => {
    setShowError(false);
  }, [errorMessage, setShowError]);

  // if error is showing, highlight inputs that need to be changed
  useEffect(() => {
    const showInputErrors = () => {
      // Only handle non-email fields since email is now handled by styled components
      errors.forEach((key) => {
        const el = document.getElementById(key);

        // Skip email field as it's handled by styled components
        if (showError && el && key !== 'email') {
          el.style.border = "1px solid red";
        }
        if (state["secondaryInfo"] !== "default") {
          const infoInput = document.getElementById(state["secondaryInfo"]);

          if (showError && infoInput && key === "secondaryInfoInput") {
            infoInput.style.border = "1px solid red";
          }
        }
      });
    };

    showInputErrors();
  }, [state, showError, errors]);

  const { setEmailInfo } = useEmailInfo();

  // Email validation function
  const validEmail = (email) => {
    var re =
      /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
    return re.test(email);
  };

  // Validate individual fields and update field errors
  const validateField = (key, value) => {
    const newFieldErrors = { ...fieldErrors };
    
    if (key === 'email' && value) {
      if (!validEmail(value)) {
        newFieldErrors.email = 'Please enter a valid email';
      } else {
        delete newFieldErrors.email;
      }
    } else if (key === 'email' && !value) {
      delete newFieldErrors.email; // Don't show validation error if field is empty
    }
    
    setFieldErrors(newFieldErrors);
  };

  // Handle input changes with validation
  const handleInputChange = (key, value) => {
    dispatch({ key, value });
    validateField(key, value);
  };

  // validate inputs
  const validateFields = () => {
    const { email, patientName } = state;

    if (!email || !patientName) {
      setErrorMessage("Please complete all mandatory fields to continue");
      return false;
    }

    const isValidEmail = validEmail(email);

    if (!isValidEmail) {
      setErrorMessage("Invalid email address");
      setErrors(["email"]);
      return false;
    }

    return true;
  };

  useEffect(() => {
    // check if every (non-optional) field has a value
    const unentered = Object.keys(state).filter((item) => {
      if (optionalKeys.includes(item)) {
        return false;
      }
      return state[item] === "";
    });

    // if all items have values and it passes validation enable the button, otherwise disable
    if (unentered.length > 0) {
      setIsError(true);
      setErrorMessage("Please complete all mandatory fields to continue");
      const errors = Object.keys(state).filter((item) => {
        return state[item] === "";
      });

      setErrors(errors);
    } else {
      const isValidated = validateFields();

      if (isValidated) {
        setIsError(false);
      } else {
        setIsError(true);
      }
    }

    setEmailInfo(state);
  }, [state, optionalKeys, setEmailInfo, setIsError]);

  return (
    <Wrapper>
      <form id="email-submit-form" action="https://y0eqv28o73.execute-api.us-east-1.amazonaws.com/PROD/static-site-mailer-dev-staticSiteMailer" method="POST">
        {inputs.map((item) => {
          const { label, placeholder, key, name } = item;
          const hasError = Boolean(fieldErrors[key]);
          const errorMessage = fieldErrors[key];
          const isEmailField = key === 'email';

          return (
            <div key={key}>
              <StyledLabel hasError={hasError}>{label}*</StyledLabel>
              <StyledInput
                id={key}
                value={state[key]}
                onChange={(e) => handleInputChange(key, e.target.value)}
                type={isEmailField ? "email" : "text"}
                name={name}
                placeholder={placeholder}
                hasError={hasError}
                className={hasError ? 'input-error' : ''}
              />
              {hasError && isEmailField && (
                <FieldErrorMessage>{errorMessage}</FieldErrorMessage>
              )}
            </div>
          );
        })}
      </form>
      <ErrorContainer>{showError && errorMessage}</ErrorContainer>
    </Wrapper>
  );
}

const Wrapper = styled.div`
  color: #000;

  padding: 28px 16px;

  @media ${devices.tablet} {
    padding: 28px 72px;
    margin: 0 auto;
  }
  @media ${devices.desktop} {
    max-width: 900px;
    padding: 0 72px;
  }

  form {
    display: flex;
    flex-direction: column;
    flex-wrap: wrap;

    @media ${devices.tablet} {
      max-height: 416px;
      column-gap: 24px;
      flex-direction: row;
    }
  }

  label {
    margin: 0px;
    margin-bottom: 8px;
  }

  option {
    font-size: 16px;
  }
  form > div {
    display: flex;
    flex-direction: column;
    margin-bottom: 8px;

    @media ${devices.tablet} {
      width: calc(50% - 12px);

      &:nth-child(2n + 1) {
        padding-right: 28px;
      }
      &:nth-child(2n) {
        padding-left: 28px;
      }
    }
  }
`;

const ErrorContainer = styled.div`
  font-size: 12px;
  margin-top: 8px;
  margin-bottom: -32px;
  min-height: 40px;
  color: red;
`;

const StyledLabel = styled.label`
  color: ${props => props.hasError ? '#e21143' : '#000'} !important;
  margin: 0px;
  margin-bottom: 8px;
`;

const StyledInput = styled.input`
  font-family: "source-sans-pro", -apple-system, BlinkMacSystemFont, "Segoe UI",
    "Roboto", "Oxygen", "Ubuntu", "Cantarell", "Fira Sans", "Droid Sans",
    "Helvetica Neue", sans-serif !important;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  border: 1px solid ${props => props.hasError ? '#e21143' : '#D4CCBD'} !important;
  border-radius: 3px;
  color: #262F39;
  padding: 12px;
  /* Prevent iOS Safari auto-zoom on focus for text inputs */
  font-size: 16px;
  line-height: 20px;

  @media ${devices.tablet} {
    font-size: 12px;
  }

  &:hover, &:focus {
    outline: none;
    box-shadow: 0px 1px 2px rgba(60, 64, 67, 0.3), 0px 1px 3px 1px rgba(60, 64, 67, 0.15);
  }
  
  &:focus {
    border: 1px solid ${props => props.hasError ? '#e21143' : defaultTheme.backgroundSecondary} !important;
  }
  
  &:not(:focus) {
    &:hover{
      border: 1px solid ${props => props.hasError ? '#e21143' : '#f399c7'} !important;
    }
  }

  &::placeholder {
    color: #48586A;
  }

  &.input-error {
    border: 1px solid #e21143 !important;
    
    &:hover, &:focus {
      border: 1px solid #e21143 !important;
    }
  }
`;

const FieldErrorMessage = styled.div`
  color: #e21143;
  font-size: 12px;
  margin-top: 4px;
`;
